// @ts-strict

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { BaseApiResponse } from '../../models/core-models';
import { ApiDocumentationResponse, IPostbackDataResponse } from './postback-api-config.service.types';
import { IPostbackConfigFields, IPostbackData, PostbackSecurityTokenResponse } from '../../types/postback-api-config/postback-api-config.service.types';

@Injectable({
  providedIn: 'root'
})
export class PostbackApiConfigService {
  public createdPostbackSubject = new Subject<IPostbackData>();
  public removedPostbackIDSubject = new Subject<number>();
  public postbackSecurityKey = new BehaviorSubject<string>('');
  public apiToken = new BehaviorSubject<string>('');

  constructor(private readonly http: HttpClient) {}

  // Refresh page after adding new configuration
  public setCreatedPostbackData(createdPostback: IPostbackData): void {
    this.createdPostbackSubject.next(createdPostback);
  }
  public setRemovedPostbackData(removedPostbackId: number): void {
    this.removedPostbackIDSubject.next(removedPostbackId);
  }

  public setPostbackSecurityKey(key: string): void {
    this.postbackSecurityKey.next(key);
  }

  public setApiToken(key: string): void {
    this.apiToken.next(key);
  }

  // Post new banner configuration on server
  public addPostbackAPIConfiguration(
    leadStatus: number,
    url: string,
    programID?: number,
    configurationId?: number
  ): Observable<BaseApiResponse<{ postback?: IPostbackData }>> {
    const postbackAPIConfiguration = {
      lead_status: leadStatus,
      postback_url: url,
      program_id: programID,
      configuration_id: configurationId
    };

    return this.http.post<BaseApiResponse<{ postback?: IPostbackData }>>(environment.apiUrl + `/postback/store`, postbackAPIConfiguration);
  }

  // Get banner configurations
  public getPostbackAPIConfigurations(programID?: number) {
    const params: Record<string, string | number> = {};
    if (programID) {
      params.program_id = programID;
    }

    return this.http.get<BaseApiResponse<IPostbackDataResponse>>(environment.apiUrl + '/postback/list', { params });
  }

  public getFieldsToCreate(programID?: number): Observable<BaseApiResponse<IPostbackConfigFields>> {
    const params: Record<string, string | number> = {};
    if (programID) {
      params.program_id = programID;
    }

    return this.http.get<BaseApiResponse<IPostbackConfigFields>>(environment.apiUrl + `/postback/create`, { params });
  }

  public editPostbackUrl(
    postbackID: number,
    postbackURL: string,
    leadStatus: number,
    configurationId?: number
  ): Observable<BaseApiResponse<{ postback?: IPostbackData }>> {
    const postbackAPIConfiguration = {
      lead_status: leadStatus,
      postback_url: postbackURL,
      postback_id: postbackID,
      configuration_id: configurationId
    };

    return this.http.put<BaseApiResponse<{ postback?: IPostbackData }>>(environment.apiUrl + `/postback/update`, postbackAPIConfiguration);
  }

  public getFieldsToEdit(postbackID: number): Observable<BaseApiResponse<IPostbackConfigFields>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<IPostbackConfigFields>>(environment.apiUrl + `/postback/edit/${postbackID}`, httpOptions);
  }

  public deletePostback(postbackID: number): Observable<BaseApiResponse<{ message?: string }>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.delete<BaseApiResponse<{ message?: string }>>(environment.apiUrl + `/postback/delete/${postbackID}`, httpOptions);
  }

  public testPostback(postbackID: number, params: Record<string, string>): Observable<BaseApiResponse<{ message?: string }>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params
    };

    return this.http.get<BaseApiResponse<{ message?: string }>>(environment.apiUrl + `/postback/test/${postbackID}`, httpOptions);
  }

  public generateSecurityToken(): Observable<PostbackSecurityTokenResponse> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<PostbackSecurityTokenResponse>(environment.apiUrl + `/postback/security_token/generate`, httpOptions);
  }

  public getApiToken(): Observable<BaseApiResponse<{ user_external_api_key: string }>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<{ user_external_api_key: string }>>(environment.apiUrl + `/user/external_api/get`, httpOptions);
  }

  public getApiDocumentation(): Observable<ApiDocumentationResponse> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<ApiDocumentationResponse>(environment.apiUrl + `/api/documentation`, httpOptions);
  }

  public generateNewAPIToken(): Observable<BaseApiResponse<{ user_external_api_key: string }>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<BaseApiResponse<{ user_external_api_key: string }>>(environment.apiUrl + `/user/external_api/generate`, httpOptions);
  }
}
